import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { branchService } from '@/services/branchService'
import { salesReportService } from '@/services/salesReportService'
import type { BranchEntity } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import type { SalesReportRow } from '@/modules/inventory/reports/types'

const SALES_PRINT_COLUMN_COUNT = 19
const PRINT_LANDSCAPE_MIN_COLUMNS = 10
const useLandscapeForPrint = SALES_PRINT_COLUMN_COUNT >= PRINT_LANDSCAPE_MIN_COLUMNS

const formatDate = (value?: string) => {
  if (!value) return '-'
  try {
    const date = new Date(value.includes('T') ? value : `${value}T12:00:00`)
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return value
  }
}

const formatQty = (n: number) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 })

const formatMoney = (n: number) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const buildAddress = (entity?: Partial<BranchEntity> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, unknown>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && String(part).trim().length > 0).join(', ')
}

export default function SalesReportPrint(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''
  const branchIdParam = searchParams.get('branchId')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<SalesReportRow[]>([])
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [, setBranchCityName] = useState<string>('')

  useEffect(() => {
    if (!fromDate || !toDate || !branchIdParam) {
      setError('Missing required parameters')
      setLoading(false)
      return
    }

    const loadReport = async () => {
      try {
        setLoading(true)
        setError(null)

        const branchId = Number(branchIdParam)
        if (!branchId) {
          setError('Invalid branch')
          setLoading(false)
          return
        }

        const fromIso = fromDate.includes('T') ? fromDate : `${fromDate}T00:00:00.000Z`
        const toIso = toDate.includes('T') ? toDate : `${toDate}T23:59:59.999Z`

        const [reportData, branchData] = await Promise.all([
          salesReportService.getSalesReport({
            fromDate: fromIso,
            toDate: toIso,
            pageNumber: 1,
            pageSize: 10000,
          }),
          branchService.getBranchById(branchId).catch(() => null),
        ])

        setRows(reportData?.data || [])
        setBranch(branchData)

        if (branchData?.country && branchData?.city) {
          try {
            const branchCities = await branchService.getCityLookups(branchData.country)
            const branchCity = branchCities.find((c: { id: number }) => c.id === branchData.city)
            if (branchCity) setBranchCityName((branchCity as { lookupNameEn?: string }).lookupNameEn || '')
          } catch {
            // silent
          }
        }
      } catch (err: unknown) {
        const e = err as { response?: { data?: { message?: string } }; message?: string }
        const message = e?.response?.data?.message || e?.message || 'Unable to load report'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    void loadReport()
  }, [fromDate, toDate, branchIdParam])

  useEffect(() => {
    const id = 'erp-sales-report-print-page'
    document.getElementById(id)?.remove()
    const el = document.createElement('style')
    el.id = id
    el.textContent = `@media print { @page { size: ${useLandscapeForPrint ? 'A4 landscape' : 'A4 portrait'}; margin: 6mm; } }`
    document.head.appendChild(el)
    return () => {
      document.getElementById(id)?.remove()
    }
  }, [])

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.qty += r.soldQuantity || 0
        acc.cost += r.totalCostAmount || 0
        acc.taxable += r.totalTaxableAmount || 0
        acc.tax += r.totalTaxAmount || 0
        acc.discount += r.discountAmount || 0
        acc.grand += r.totalAmountWithTax || 0
        return acc
      },
      { qty: 0, cost: 0, taxable: 0, tax: 0, discount: 0, grand: 0 },
    )
  }, [rows])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing report for print...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error}</p>
        <Button onClick={() => navigate('/inventory/reports/sales')}>Back to Report</Button>
      </div>
    )
  }

  return (
    <div className="ledger-print-root w-full max-w-none p-6 bg-gray-50 print:bg-white">
      <style>{`
        @media print {
          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #root {
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body .min-h-screen.flex > div.fixed.inset-0 {
            display: none !important;
          }
          body aside,
          body header.border-b {
            display: none !important;
          }
          body .min-h-screen.flex > div.flex-1.min-w-0,
          body .min-h-screen.flex > div.flex-1 {
            margin-left: 0 !important;
            margin-right: 0 !important;
            margin-inline: 0 !important;
            width: 100% !important;
            max-width: none !important;
          }
          body main.flex-1 {
            max-width: none !important;
            width: 100% !important;
            overflow: visible !important;
          }
          .no-print { display: none !important }
          .ledger-print-root {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
          }
          .ledger-wrapper {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 3mm 4mm !important;
            border: none !important;
            page-break-after: avoid;
          }
          .ledger-table {
            width: 100% !important;
            font-size: 7px !important;
          }
          .ledger-table th,
          .ledger-table td {
            font-size: 7px !important;
            padding: 2px 3px !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }

        .ledger-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937 }
        .ledger-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: 0.5px solid #ddd }
        .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px }
        .ledger-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .divider-strong { height:3px; background:#0A1E60; margin:6px 0; }

        .date-filter { display:flex; justify-content:space-between; flex-wrap:wrap; background:#E5E7EB; padding:8px; margin:8px 0; border-radius:4px; gap:4px }
        .date-item { flex:1; min-width:120px; text-align:center }

        .ledger-table { width:100%; border-collapse:collapse; font-size:7px; margin-top:4px }
        .ledger-table th, .ledger-table td { border:1px solid #F5F5F5; padding:2px 3px; }
        .ledger-table thead th { background:#F5F5F5; font-weight:700; font-size:7px }
        .ledger-table tbody tr td { vertical-align:middle; font-size:7px }
        .ledger-total-row { background:#F5F5F5; font-weight:800 }

        @media screen {
          .ledger-wrapper { max-width: 1200px; padding: 12px }
          .ledger-main-title { font-size: 18px; margin-top: 6px }
          .ledger-table { font-size: 8px; margin-top: 6px }
          .ledger-table th, .ledger-table td { padding: 3px 4px }
          .date-filter { padding: 10px; margin: 10px 0 }
        }

        .rtl { direction: rtl; unicode-bidi: embed }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/inventory/reports/sales')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Report
        </Button>
      </div>

      <div className="ledger-wrapper">
        <div className="top-brand">
          <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
            <div>
              <strong>{branch?.nameEn || '-'}</strong>
            </div>
            <div style={{ marginTop: 4 }}>
              <strong>Address:</strong> {buildAddress(branch) || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Email:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Phone:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>CR No:</strong> {branch?.crNo || '-'}
            </div>
          </div>

          <div
            style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
          >
            <img src={getLogoPath()} alt="Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
          </div>

          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px] rtl">
            <div>
              <strong>{branch?.nameAr || branch?.nameEn || '-'}</strong>
            </div>
            <div style={{ marginTop: 4 }}>
              <strong>العنوان:</strong> {branch?.addressAr || buildAddress(branch) || '--'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>البريد الإلكتروني:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>هاتف:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>رقم تسجيل الشركة:</strong> {branch?.crNo || '-'}
            </div>
          </div>
        </div>

        <div className="divider-strong print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

        <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
          <div className="ledger-main-title print:text-base" style={{ fontSize: 18, fontWeight: 700 }}>
            Sales Report
          </div>
        </div>

        <div className="date-filter print:my-2" style={{ backgroundColor: '#E5E7EB', padding: '8px', margin: '8px 0', borderRadius: '4px' }}>
          <div className="date-item" style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 9, marginBottom: 2 }}>▼ FROM DATE</div>
            <div style={{ fontSize: 10 }}>{formatDate(fromDate)}</div>
          </div>
          <div className="date-item" style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 9, marginBottom: 2 }}>▼ TO DATE</div>
            <div style={{ fontSize: 10 }}>{formatDate(toDate)}</div>
          </div>
        </div>

        <table className="ledger-table">
          <thead>
            <tr>
              <th>DATE</th>
              <th>ITEM CODE</th>
              <th>NAME</th>
              <th>CATEGORY</th>
              <th>SUB CAT</th>
              <th>UOM</th>
              <th>TYPE</th>
              <th>CUSTOMER</th>
              <th>INV NO</th>
              <th>WH</th>
              <th>BATCH</th>
              <th style={{ textAlign: 'right' }}>QTY</th>
              <th style={{ textAlign: 'right' }}>U COST</th>
              <th style={{ textAlign: 'right' }}>U SALE</th>
              <th style={{ textAlign: 'right' }}>T COST</th>
              <th style={{ textAlign: 'right' }}>TAXABLE</th>
              <th style={{ textAlign: 'right' }}>TAX</th>
              <th style={{ textAlign: 'right' }}>DISC</th>
              <th style={{ textAlign: 'right' }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={19} style={{ textAlign: 'center', padding: 16 }}>
                  No sales lines available
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={`${r.invoiceNo}-${r.batchCode}-${idx}`}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.itemCode || '-'}</td>
                  <td>{r.itemName || '-'}</td>
                  <td>{r.category || '-'}</td>
                  <td>{r.subCategory || '-'}</td>
                  <td>{r.uom || '-'}</td>
                  <td>{r.type || '-'}</td>
                  <td>{r.customer || '-'}</td>
                  <td>{r.invoiceNo || '-'}</td>
                  <td>{r.warehouse?.trim() || '-'}</td>
                  <td>{r.batchCode || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{formatQty(r.soldQuantity)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.unitCostPrice)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.unitSalesPrice)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.totalCostAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.totalTaxableAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.totalTaxAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.discountAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(r.totalAmountWithTax)}</td>
                </tr>
              ))
            )}
            <tr className="ledger-total-row">
              <td colSpan={11} style={{ textAlign: 'right', fontWeight: 700 }}>
                TOTAL
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatQty(totals.qty)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>—</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>—</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals.cost)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals.taxable)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals.tax)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals.discount)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(totals.grand)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
